import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Show, SignInButton, UserButton } from "@clerk/react";

import { useAppUser } from "../auth/AppUserProvider";
import BrandWordmark from "./BrandWordmark";
import { Permission, ROLE_LABELS } from "../auth/permissions";
import EntitlementBadge from "./EntitlementBadge";
import "./Navbar.css";

/**
 * Top navigation.
 *
 * Three tiers of link:
 *
 *  - always shown: Home, Market, Converter, About — public pages that read
 *    public market data and need no account;
 *  - shown with `goldbot:read`: the GoldBot Control Center;
 *  - shown with `myteam:read`: My Team;
 *  - shown with `users:read`: Admin.
 *
 * Which of these a user has is the *effective* permission set the backend
 * calculated — role defaults plus any per-user override — so a link granted
 * by an override appears without anything here knowing that is why.
 *
 * The permission-gated links are hidden according to the role `/api/me`
 * reported. That is a convenience, not a control: the backend refuses anything
 * the role does not allow, whether or not the link was rendered. Typing
 * `/admin/users` by hand gets a 403 from the API just the same.
 *
 * Below 900px the links do not fit on one line beside the brand and the
 * account block, so they collapse into a drop-down panel behind a toggle. The
 * links themselves — and which of them render — are identical either way; only
 * where they sit changes. The account block and the Clerk `<UserButton>` stay
 * in the bar at every width, so signing in or out is always one tap.
 */
function Navbar() {
  const { appUser, hasPermission } = useAppUser();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  // Tapping a link navigates; the panel has to get out of the way by itself.
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMenuOpen) {
      return undefined;
    }

    function onKeyDown(event) {
      if (event.key === "Escape") {
        setIsMenuOpen(false);
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isMenuOpen]);

  const displayName =
    appUser && [appUser.first_name, appUser.last_name].filter(Boolean).join(" ");

  return (
    <nav className={`navbar${isMenuOpen ? " is-open" : ""}`}>
      {/* LEFT SIDE */}
      <Link to="/" className="navbar-left" aria-label="OMJ VISION home">
        <img src="/favicon.svg" alt="" width="26" height="26" className="navbar-mark" />
        <BrandWordmark />
      </Link>

      {/* RIGHT SIDE */}
      <div className="navbar-right">
        <div className="navbar-links" id="navbar-links">
          <Link to="/">Home</Link>
          <Link to="/dashboard">Market</Link>
          <Link to="/converter">Converter</Link>

          {hasPermission(Permission.GOLDBOT_READ) ? (
            <Link to="/goldbot">GoldBot</Link>
          ) : null}

          {hasPermission(Permission.MY_TEAM_READ) ? (
            <Link to="/my-team">My Team</Link>
          ) : null}

          <Link to="/about">About</Link>

          {hasPermission(Permission.USERS_READ) ? (
            <Link to="/admin/users" className="navbar-admin">
              Admin
            </Link>
          ) : null}
        </div>

        <div className="navbar-account">
          <Show when="signed-out">
            <SignInButton mode="redirect">
              <button type="button" className="btn-outline">
                Sign in
              </button>
            </SignInButton>
          </Show>

          <Show when="signed-in">
            <div className="navbar-user">
              {appUser ? (
                <>
                  <EntitlementBadge entitlement={appUser.entitlement} />
                  <span className="navbar-user__identity">
                    <strong>{displayName || appUser.email}</strong>
                    <span>
                      {appUser.email}
                      {ROLE_LABELS[appUser.role] ? ` · ${ROLE_LABELS[appUser.role]}` : ""}
                    </span>
                  </span>
                </>
              ) : null}
              {/* Clerk's menu owns "Sign out" and the profile screen. */}
              <UserButton afterSignOutUrl="/sign-in" />
            </div>
          </Show>
        </div>

        <button
          type="button"
          className="navbar-toggle"
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={isMenuOpen}
          aria-controls="navbar-links"
          onClick={() => setIsMenuOpen((open) => !open)}
        >
          <span className="navbar-toggle__bars" aria-hidden="true" />
        </button>
      </div>

      {/* Tapping anywhere off the panel dismisses it. Rendered only while the
          panel is open, and only reachable below 900px. */}
      {isMenuOpen ? (
        <button
          type="button"
          className="navbar-backdrop"
          tabIndex={-1}
          aria-hidden="true"
          onClick={() => setIsMenuOpen(false)}
        />
      ) : null}
    </nav>
  );
}

export default Navbar;
